import React, { useCallback, useEffect, useRef, useState } from "react";
import { KeyboardDatePicker } from "@material-ui/pickers";
import moment from "moment";
import { Box, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EventIcon from "@material-ui/icons/Event";
import * as customColors from "../../../../../theme/customColors";
import { HAKLAI_DATE_PICKER_ERRORS } from "../haklaiSharedTexts";

const useStyles = makeStyles((theme) => ({
  datePicker: {
    "& .MuiInputBase-root": {
      borderRadius: "20px"
    },
    "& .MuiOutlinedInput-input": {
      boxSizing: "border-box",
      fontSize: "13px",
      minWidth: 0,
      paddingLeft: "8px",
      paddingRight: "4px"
    },
    "& .MuiOutlinedInput-input::placeholder": {
      fontSize: "13px",
      opacity: 1
    },
    "& .MuiInputAdornment-root": {
      marginLeft: 0,
      marginRight: 0
    },
    "& .MuiIconButton-root": {
      padding: "4px"
    }
  },
  tooltipErrorWrap: {
    alignItems: "center",
    display: "inline-flex",
    flexShrink: 0,
    gap: 6,
    position: "relative"
  },
  tooltipErrorInputSlot: {
    flex: "0 0 auto"
  },
  tooltipErrorIcon: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    backgroundColor: "#d32f2f",
    color: "#fff",
    fontSize: "0.65rem",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    userSelect: "none",
    cursor: "help"
  },
  tooltipErrorIconHidden: {
    left: 0,
    pointerEvents: "none",
    position: "absolute",
    visibility: "hidden",
    width: 0
  }
}));

const DATE_FIELD_MIN_WIDTH = 148;

const DatePickerField = function (props) {
  const classes = useStyles();
  const lastPropValueRef = useRef(props.value);
  const preserveInvalidInputOnNextSyncRef = useRef(false);
  const [localValidationError, setLocalValidationError] = useState("");
  const invalidInputMessage = props.invalidInputMessage || HAKLAI_DATE_PICKER_ERRORS.invalidDate;
  const validateInvalidInputOnBlur = props.validateInvalidInputOnBlur === true;

  // Convert YYYY-MM-DD string to moment object (or null)
  const valueToMoment = useCallback((value) => {
    if (!value || value === "" || value === undefined || value === null) return null;
    try {
      if (moment.isMoment(value)) return value.isValid() ? value : null;
      if (value instanceof Date) return moment(value);

      const strictYmd = moment(value, "YYYY-MM-DD", true);
      if (strictYmd.isValid()) return strictYmd;

      const strictDmy = moment(value, "DD/MM/YYYY", true);
      if (strictDmy.isValid()) return strictDmy;

      const fallback = moment(value);
      return fallback.isValid() ? fallback : null;
    } catch {
      return null;
    }
  }, []);

  // Convert moment object to YYYY-MM-DD string (or empty string)
  const momentToValue = (momentObj) => {
    if (!momentObj || !momentObj.isValid()) return "";
    return momentObj.format("YYYY-MM-DD");
  };

  const formatDisplayValue = useCallback((value) => {
    const parsed = valueToMoment(value);
    return parsed ? parsed.format("DD/MM/YYYY") : "";
  }, [valueToMoment]);

  const [inputValue, setInputValue] = useState(formatDisplayValue(props.value));

  useEffect(() => {
    if (props.value !== lastPropValueRef.current) {
      lastPropValueRef.current = props.value;
      if (preserveInvalidInputOnNextSyncRef.current) {
        preserveInvalidInputOnNextSyncRef.current = false;
        return;
      }
      setLocalValidationError("");
      setInputValue(formatDisplayValue(props.value));
    }
  }, [formatDisplayValue, props.value]);

  const handleChange = (date, keyboardInputValue) => {
    if (props.onChange) {
      try {
        const hasValidDate = date && moment(date).isValid();
        const isCleared = keyboardInputValue === "" || keyboardInputValue === null || keyboardInputValue === undefined;

        if (keyboardInputValue !== undefined && keyboardInputValue !== null) {
          setInputValue(keyboardInputValue);
        } else if (hasValidDate) {
          setInputValue(moment(date).format("DD/MM/YYYY"));
        }

        // Only update external state when we have a valid date or the user cleared the input.
        // This preserves partially typed values in the picker UI.
        if (hasValidDate || isCleared) {
          const value = hasValidDate ? momentToValue(moment(date)) : "";
          setLocalValidationError("");
          // Create synthetic event to match expected format
          const syntheticEvent = {
            target: {
              name: props.name,
              value: value
            }
          };
          props.onChange(syntheticEvent);
        }
      } catch {
        setLocalValidationError(invalidInputMessage);
      }
    }
  };

  const handleBlur = () => {
    if (validateInvalidInputOnBlur) {
      const parsedInputValue = inputValue ? moment(inputValue, ["DD/MM/YYYY", "YYYY-MM-DD"], true) : null;
      const hasValidInputValue = Boolean(parsedInputValue && parsedInputValue.isValid());
      const nextValue = hasValidInputValue ? momentToValue(parsedInputValue) : "";

      if (inputValue && !hasValidInputValue) {
        setLocalValidationError(invalidInputMessage);
        if (props.onChange) {
          preserveInvalidInputOnNextSyncRef.current = true;
          props.onChange({
            target: {
              name: props.name,
              value: ""
            }
          });
        }
      } else {
        setLocalValidationError("");
        if (hasValidInputValue && props.onChange && nextValue !== props.value) {
          props.onChange({
            target: {
              name: props.name,
              value: nextValue
            }
          });
        }
        if (hasValidInputValue) {
          setInputValue(formatDisplayValue(nextValue));
        }
      }

      if (props.onBlur) {
        const syntheticEvent = {
          target: {
            name: props.name,
            value: nextValue
          }
        };
        props.onBlur(syntheticEvent);
      }
      return;
    }

    if (props.onBlur) {
      const syntheticEvent = {
        target: {
          name: props.name,
          value: props.value || ""
        }
      };
      props.onBlur(syntheticEvent);
    }
  };

  // Set background color (matching LabeledTextField logic)
  let background = customColors.white;
  if (props.background) background = customColors.orange;
  else if (props.disabled) background = customColors.veryLigntGray;

  const requestedWidth = props.textFieldWidth || 190;
  const textFieldWidth = Math.max(requestedWidth, DATE_FIELD_MIN_WIDTH) + "px";

  // Convert minDate/maxDate from YYYY-MM-DD strings to moment objects
  const minDate = props.minDate ? moment(props.minDate, "YYYY-MM-DD", true) : null;
  const maxDate = props.maxDate ? moment(props.maxDate, "YYYY-MM-DD", true) : null;

  const momentValue = localValidationError ? null : valueToMoment(props.value);
  const combinedClassName = [classes.datePicker, props.className].filter(Boolean).join(" ");
  const externalErrorMessage = props.error && typeof props.helperText === "string" ? props.helperText : "";
  const tooltipErrorMessage = localValidationError || externalErrorMessage;
  const useTooltipErrorDisplay = props.errorDisplay === "tooltip";
  const suppressInternalHelperText = useTooltipErrorDisplay || props.suppressHelperText === true;
  const helperText = suppressInternalHelperText ? "" : (localValidationError || props.helperText);
  const error = Boolean(localValidationError) || props.error;

  const hasHelperTextTuning = props.helperTextMinHeight !== undefined || props.centerHelperText === true;
  const helperTextStyle = hasHelperTextTuning
    ? {
        lineHeight: 1.25,
        whiteSpace: "normal",
        minHeight: props.helperTextMinHeight || undefined,
        height: "auto",
        overflow: "visible",
        ...(props.centerHelperText
          ? {
              textAlign: "center",
              marginLeft: 0,
              marginRight: 0
            }
          : {})
      }
    : undefined;

  // Build picker props conditionally
  const pickerProps = {
    className: combinedClassName,
    disabled: props.disabled,
    size: "small",
    format: "DD/MM/YYYY",
    value: momentValue,
    inputValue: inputValue,
    onChange: handleChange,
    onBlur: handleBlur,
    inputRef: props.inputRef,
    error,
    helperText,
    FormHelperTextProps: helperTextStyle ? { style: helperTextStyle } : undefined,
    invalidDateMessage: "",
    inputVariant: "outlined",
    fullWidth: true,
    inputProps: {
      ...(props.inputProps || {}),
      ...(props.id ? { id: props.id } : {}),
      name: props.name,
      placeholder: props.placeholder || "DD/MM/YYYY",
      dir: "ltr"
    },
    InputProps: {
      style: {
        background: background,
        borderRadius: "20px"
      }
    },
    InputLabelProps: {
      shrink: false
    },
    InputAdornmentProps: {
      position: "start"
    },
    keyboardIcon: <EventIcon />,
    clearable: false,
    autoOk: true
  };

  // Only add minDate/maxDate if they're valid
  if (minDate && minDate.isValid()) {
    pickerProps.minDate = minDate;
  }
  if (maxDate && maxDate.isValid()) {
    pickerProps.maxDate = maxDate;
  }

  if (useTooltipErrorDisplay) {
    return (
      <Box className={classes.tooltipErrorWrap}>
        <Box className={classes.tooltipErrorInputSlot} style={{ width: textFieldWidth }}>
          <KeyboardDatePicker {...pickerProps} />
        </Box>
        <Tooltip
          title={tooltipErrorMessage || ""}
          placement="top"
          disableHoverListener={!error}
          disableFocusListener={!error}
          disableTouchListener={!error}
        >
          <span
            className={[
              classes.tooltipErrorIcon,
              !error ? classes.tooltipErrorIconHidden : ""
            ].filter(Boolean).join(" ")}
          >
            !
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <div style={{ width: textFieldWidth }}>
      <KeyboardDatePicker {...pickerProps} />
    </div>
  );
};

export default DatePickerField;
